import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, MapPin } from 'lucide-react';
import { ServicePageLayout } from '../components/ServicePageLayout';
import { NotFoundPage } from './NotFoundPage';
import { getCity } from '../data/cityData';
import { packages } from '../data/packages';
import { BUSINESS_ID, cityPath } from '../utils/seo';
/** City-specific wording for each package on featured city pages. */
const PACKAGE_COPY: Record<string, (city: string) => string> = {
  'basic-wash': city => `A maintenance wash in ${city} covering the exterior, a light wipe-down, vacuum, rims, tires, and windows.`,
  'wash-wax': city => `Wash and wax in ${city} — an exterior wash, vacuum, light interior wipe-down, tire dressing, and hybrid ceramic spray for gloss and water beading.`,
  'full-detail': city => `Our most popular full detail in ${city}: the complete interior detail plus exterior wash, clay bar treatment, rims, tires, and hybrid ceramic spray.`,
  'interior-detail': city => `Mobile interior detailing in ${city} — shampooed seats and floor mats, a deep interior clean, vacuum, and steam sanitizing.`
};
const relatedServices = [{
  label: 'Interior Detailing',
  to: '/services/interior-detailing'
}, {
  label: 'Exterior Detailing',
  to: '/services/exterior-detailing'
}, {
  label: 'Full Car Detail',
  to: '/full-detail'
}, {
  label: 'Engine Bay Detailing',
  to: '/services/engine-bay-detailing'
}, {
  label: 'Headlight Restoration',
  to: '/services/headlight-restoration'
}, {
  label: 'Mobile Detailing',
  to: '/services/mobile-detailing'
}, {
  label: 'Hybrid Ceramic Spray',
  to: '/services/hybrid-ceramic-spray'
}];
export function CityPage({
  slug: slugProp
}: {
  slug?: string;
} = {}) {
  const params = useParams<{
    slug: string;
  }>();
  const slug = slugProp ?? params.slug;
  const city = slug ? getCity(slug) : undefined;
  // An unknown city slug used to silently redirect to the homepage, which
  // search engines read as a soft 404 (a real page answering an invalid URL).
  // Serve the noindexed 404 instead.
  if (!city) {
    return <NotFoundPage data-id="element-629" data-mp="1nxpqrrdqfr5p" />;
  }
  const baseUrl = 'https://www.fulldetailpro.com';
  const path = cityPath(city.slug);
  // Described as a Service delivered to this city rather than a LocalBusiness
  // located in it — Mike's Pro Detail is mobile and has no storefront anywhere.
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `Mobile Car Detailing in ${city.name}, CA`,
    serviceType: 'Mobile Car Detailing',
    description: city.metaDescription,
    url: `${baseUrl}${path}`,
    provider: {
      '@id': BUSINESS_ID
    },
    areaServed: {
      '@type': 'City',
      name: `${city.name}, CA`
    }
  };
  const breadcrumbs = [{
    name: 'Home',
    item: `${baseUrl}/`
  }, {
    name: 'Locations',
    item: `${baseUrl}/locations`
  }, {
    name: city.name,
    item: `${baseUrl}${path}`
  }];
  return <ServicePageLayout title={city.title} subtitle={city.subtitle} metaDescription={city.metaDescription} schema={schema} faqs={city.faqs} breadcrumbs={breadcrumbs} cityName={city.name} seoTitle={city.seoTitle} data-id="element-630" data-mp="2qbeudjsi1a0e">
      {city.paragraphs.map((p, i) => <p key={i} data-id="element-631" data-mp="2nx9qfmadp01d" data-mpi={i}>{p}</p>)}

      {city.featured && <>
          <h2 data-id="element-632" data-mp="2nyuh30tknyty">We Come To You</h2>
          <p data-id="element-633" data-mp="2svup3h9zfbnp">
            Skip the trip to the detailing shop. Mike's Pro Detail brings
            professional mobile detailing directly to your home, office, or
            another convenient location in {city.name}. We don't operate a
            storefront — we travel to every appointment with our own water and
            detailing equipment. We require access to a standard electrical
            outlet at the service location.
          </p>

          <h2 data-id="element-634" data-mp="3bte20vzk9efe">Detailing Packages Available in {city.name}</h2>
          <p data-id="element-635" data-mp="3encsq6gs7es">
            Every package is performed on-site, at the same prices listed on our{' '}
            <Link to="/services" data-id="element-636" data-mp="865b16h3rtw2">detailing services page</Link>. Prices shown are
            SM/Mid vehicles · SUV/Cross vehicles.
          </p>
          <ul className="space-y-4 my-6 list-none pl-0" data-id="element-637" data-mp="2go9f2ja8xgv1">
            {packages.map(pkg => <li key={pkg.slug} className="flex items-start" data-id="element-638" data-mp="2bn264gwdabms">
                <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-639" data-mp="26o8pwgpp46ix" />
                <span data-id="element-640" data-mp="26qlehcpylf4y">
                  <strong data-id="element-641" data-mp="21q777ww6m97z">
                    <Link to={pkg.path} data-id="element-642" data-mp="21qz3l9xlsazb">{pkg.name}</Link> — {pkg.priceSm} ·{' '}
                    {pkg.priceSuv}
                    {pkg.requirement ? ` (${pkg.requirement})` : ''}:
                  </strong>{' '}
                  {PACKAGE_COPY[pkg.slug]?.(city.name) ?? pkg.summary}
                </span>
              </li>)}
          </ul>
          <p data-id="element-643" data-mp="2pl9yebigkorm">
            Add-ons such as engine bay cleaning, headlight restoration, and clay
            bar treatment can be added to any visit — see the full list on our{' '}
            <Link to="/services" data-id="element-644" data-mp="3i58ildfygndh">services page</Link>.
          </p>

          <h2 data-id="element-645" data-mp="3dc5v9n1rua5q">Serving {city.name} and the Conejo Valley</h2>
          <p data-id="element-646" data-mp="3lluvutkrcezj">
            Beyond {city.name} itself, our mobile detailing service covers nearby
            communities across the Conejo Valley, including{' '}
            <Link to={cityPath('newbury-park')} data-id="element-647" data-mp="3pnygo8tbgvxh">Newbury Park</Link>,{' '}
            <Link to={cityPath('westlake-village')} data-id="element-648" data-mp="3ulhw3c1r3er0">Westlake Village</Link>, and{' '}
            <Link to={cityPath('oak-park')} data-id="element-649" data-mp="3gro113epovf">Oak Park</Link>. Wherever you are,
            we drive to you — no drop-off and no waiting room. Have a question
            first? Check our <Link to="/faq" data-id="element-650" data-mp="cgwth5p1id6y">FAQ</Link> or{' '}
            <Link to="/contact" data-id="element-651" data-mp="cflpp5vlh5fu">contact us to book</Link>.
          </p>
        </>}

      <h2 data-id="element-652" data-mp="h7d5cld7pqr7">Mobile Detailing Services in {city.name}</h2>
      <p data-id="element-653" data-mp="h9pupjlav3ea">
        We bring the full range of professional auto detailing to {city.name},
        with every service performed on-site at your home or office. Popular
        options for {city.name} drivers include:
      </p>
      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-654" data-mp="w4npsk7u26z2">
        <li className="flex items-start" data-id="element-655" data-mp="w5fedb30j4fu">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-656" data-mp="6gibfq1fkwmv" />
          <span data-id="element-657" data-mp="6enqibq5t4is">
            <strong data-id="element-658" data-mp="1hoybyq237n8">Interior Detailing:</strong> Deep vacuuming, shampooing,
            steam sanitizing, and leather conditioning for a like-new cabin.
          </span>
        </li>
        <li className="flex items-start" data-id="element-659" data-mp="g8o30ynrtc1j">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-660" data-mp="bfl5ygjsbkgl" />
          <span data-id="element-661" data-mp="p79hoiq4g6x1">
            <strong data-id="element-662" data-mp="p6q9vuu6qywq">Exterior Detailing:</strong> Safe hand wash, clay bar
            decontamination, and durable wax or sealant protection.
          </span>
        </li>
        <li className="flex items-start" data-id="element-663" data-mp="kaj1lvebhbbm">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-664" data-mp="z5pb4scmfs9p" />
          <span data-id="element-665" data-mp="u6cljzud873f">
            <strong data-id="element-666" data-mp="u5t6qs00hytm">Full Car Detail:</strong> Our most popular bumper-to-bumper
            package combining interior and exterior services.
          </span>
        </li>
        <li className="flex items-start" data-id="element-667" data-mp="30yn54ri4wqw8">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-668" data-mp="2vyr2owp357is" />
          <span data-id="element-669" data-mp="1bfx1zohuqv3l">
            <strong data-id="element-670" data-mp="1bfevlz1h70hy">Engine Bay & Headlight Restoration:</strong> Specialty
            services to keep every part of your vehicle looking its best.
          </span>
        </li>
      </ul>

      <h2 data-id="element-671" data-mp="11q56ms1xhxy2">Neighborhoods We Serve in {city.name}</h2>
      <p data-id="element-672" data-mp="11plll2gzetft">
        Our mobile detailing service reaches every corner of {city.name},
        including {city.neighborhoods.slice(0, -1).join(', ')}, and{' '}
        {city.neighborhoods[city.neighborhoods.length - 1]}. Wherever you park,
        we come to you with our own water and detailing equipment. We require
        access to a standard electrical outlet at the service location.
      </p>
      <div className="not-prose flex flex-wrap gap-3 my-6" data-id="element-673" data-mp="isu5f1wqkufz">
        {city.neighborhoods.map(n => <span key={n} className="inline-flex items-center gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-gray-300" data-id="element-674" data-mp="nqm88qyx60yy">
            <MapPin className="w-4 h-4 text-red-500" data-id="element-675" data-mp="2i3vabxp4yk76" />
            {n}
          </span>)}
      </div>

      <h2 data-id="element-676" data-mp="14x0qdtyglee2">Explore Our Detailing Services</h2>
      <p data-id="element-677" data-mp="14vz4eufwn7hk">
        Learn more about each of our professional mobile detailing services
        available in {city.name}, or head back to our{' '}
        <Link to="/" data-id="element-678" data-mp="w4ypxvoe3y3s">mobile detailing homepage</Link> for full pricing and
        packages:
      </p>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-679" data-mp="r5vkwb8etrvt">
        {relatedServices.map(s => <Link key={s.to} to={s.to} className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-680" data-mp="m6j0dx50gwwe">
            {s.label} &rarr;
          </Link>)}
      </div>

      <h2 data-id="element-681" data-mp="d6meu4dx4e5k">Book Mobile Detailing in {city.name}</h2>
      <p data-id="element-682" data-mp="879m530d8cv2">
        Call or text Mike's Pro Detail at{' '}
        <a href="tel:8183819905" data-id="element-683" data-mp="88uhguui7ztz">(818) 381-9905</a>, or{' '}
        <Link to="/contact" data-id="element-684" data-mp="27kdkytx1gfw9">request a free quote online</Link>. Compare
        packages and pricing on our{' '}
        <Link to="/services" data-id="element-685" data-mp="22mc66l6nb2ql">detailing services page</Link>, or{' '}
        <Link to="/locations" data-id="element-686" data-mp="2a51wgudrm701">view all service areas</Link>.
      </p>
    </ServicePageLayout>;
}